import { paymentStatuses, shipmentStatusLabels } from '@reveni-io/reveni-ui'

import { billingStatuses, billingTypes } from '@/billing/pages/BillingPage/Filters/constants'
import { baseReasonsByLanguage, refundStatusOptions } from '@/common/constants'
import { getSelectedRegions } from '@/common/helpers/helpers'
import { exportPriceStatusOptions } from '@/inventory/pages/InventoryPage/Filters/ExportPriceFilter/constants'
import { productStatusOptions } from '@/inventory/pages/InventoryPage/Filters/ProductStatusFilter/constants'
import { normalizedTrackingStatusMessages } from '@/parcels/constants'
import { claimStatusOptions } from '@/parcels/pages/ParcelsPage/pages/ParcelsListPage/Filters/ClaimStatusSelector/constants'
import { claimTypeOptions } from '@/parcels/pages/ParcelsPage/pages/ParcelsListPage/Filters/ClaimTypeSelector/constants'
import { parcelTypeOptions } from '@/parcels/pages/ParcelsPage/pages/ParcelsListPage/Filters/ParcelTypeSelector/constants'
import { transactionTypes } from '@/returns/constants'
import { alertTypeLabels } from '@/returns/pages/TransactionsPage/Filters/AlertTypesSelector/constants'
import { deliveryMethods, statuses } from '@/returns/pages/TransactionsPage/Filters/constants'
import { exchangedTypesOptions } from '@/returns/pages/TransactionsPage/Filters/ExchangedTypeSelector/constants'
import { exchangeForOrderStatusValues } from '@/returns/pages/TransactionsPage/Filters/ExchangeForOrderStatusFilter/constants'
import { getPolicyRuleLabel } from '@/returns/pages/TransactionsPage/Filters/PolicyRulesSelector/helpers'
import { refundMethods } from '@/returns/pages/TransactionsPage/Filters/RefundMethodSelector/constants'
import { shippingLabelStatusValues } from '@/returns/pages/TransactionsPage/Filters/ShippingLabelStatusSelector/constants'

import { chips } from './constants'

export const getChips = ({
  t,
  language,
  filters,
  returnReasons = [],
  rejectReasons = [],
  regions = [],
  productCategories = [],
  carriers = [],
  productCollections = [],
  locations = [],
  carrierServices = [],
  deliveryMethods = [],
  productTypes = [],
  policyRules = [],
  forReviewReasons = [],
}) => {
  let activeFilters = chips
    .filter(chip => chip.show(filters))
    .map(chip => ({
      ...chip,
      label: chip.label({
        filters,
        t,
        language,
        returnReasons,
        rejectReasons,
        regions,
        productCategories,
        carriers,
        productCollections,
        locations,
        carrierServices,
        deliveryMethods,
        productTypes,
        policyRules,
        forReviewReasons,
      }),
    }))

  return activeFilters
}

export const truncateList = (names, t) => {
  if (names.length <= 2) {
    return names.join(', ')
  }

  const visible = names.slice(0, 2).join(', ')
  const remaining = names.length - 2
  return `${visible} (+ ${remaining} ${t('más')})`
}

export const getTransactionTypeName = (transactionTypeValues, t) => {
  const names = []
  const transactionType = {
    [transactionTypes.refund]: 'Devolución',
    [transactionTypes.exchange]: 'Cambio',
  }

  transactionTypeValues.forEach(statusValue => {
    names.push(t(transactionType[statusValue]))
  })

  return truncateList(names, t)
}

export const getStatusesName = (selectedStatusValues = [], t) => {
  const names = []

  selectedStatusValues.forEach(statusValue => {
    const status = statuses.find(s => s.value === statusValue)

    names.push(t(status.label))
  })

  return truncateList(names, t)
}

export const getDatesLabel = (dates, t, language) => {
  const startDate = dates?.startDate
    ? new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(dates.startDate))
    : null
  const endDate = dates?.endDate
    ? new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(dates.endDate))
    : null

  if (startDate && endDate) return `${startDate} - ${endDate}`.toUpperCase()
  if (startDate) return t('Desde {{date}}', { date: startDate.toUpperCase() })
  if (endDate) return t('Hasta {{date}}', { date: endDate.toUpperCase() })
}

export const getDeliveryMethodTypeNames = (deliveryMethodValues = [], t) => {
  const names = []

  deliveryMethodValues.forEach(deliveryMethodValue => {
    const deliveryMethod = deliveryMethods.find(dm => dm.value === deliveryMethodValue)

    names.push(t(deliveryMethod.label))
  })

  return truncateList(names, t)
}

export const getDeliveryMethodName = (deliveryMethodId, deliveryMethods = []) => {
  const deliveryMethod = deliveryMethods.find(dm => dm.id === deliveryMethodId)
  if (!deliveryMethod) return deliveryMethodId
  return `${deliveryMethod.internal_id} | ${deliveryMethod.internal_name}`
}

export const getRefundMethodNames = (selectedRefundMethodValues = [], t) => {
  const names = []

  selectedRefundMethodValues.forEach(refundMethodValue => {
    const refundMethod = refundMethods.find(s => s.value === refundMethodValue)

    names.push(t(refundMethod.label))
  })

  return truncateList(names, t)
}

export const getReturnReasonNames = (selectedReturnReasonValues = [], language = 'en', returnReasons, t) => {
  const names = []

  selectedReturnReasonValues.forEach(returnReasonValue => {
    const returnReason = returnReasons.find(r => r.id === returnReasonValue)
    const label = returnReason?.display[language] || baseReasonsByLanguage?.[returnReason?.key]?.[language]
    if (label) names.push(label)
  })

  return truncateList(names, t)
}

export const getReturnReasonTypeNames = (selectedReturnReasonTypeValues = [], language = 'en', t) => {
  const names = selectedReturnReasonTypeValues.map(key => baseReasonsByLanguage?.[key]?.[language]).filter(Boolean)

  return truncateList(names, t)
}

export const getRejectReasonNames = (selectedRejectReasonValues = [], rejectReasons, t) => {
  const names = []

  selectedRejectReasonValues.forEach(rejectReasonValue => {
    const rejectReason = rejectReasons.find(r => r.value === rejectReasonValue)
    if (rejectReason) names.push(t(rejectReason.label))
  })

  return truncateList(names, t)
}

export const getForReviewReasonNames = (selectedForReviewReasonValues = [], forReviewReasons, t) => {
  const names = []

  selectedForReviewReasonValues.forEach(forReviewReasonValue => {
    const forReviewReason = forReviewReasons.find(r => r.value === forReviewReasonValue)
    if (forReviewReason) names.push(t(forReviewReason.label))
  })

  return truncateList(names, t)
}

export const getRegionsNames = (selectedRegionIds = [], regions, t) => {
  const names = []

  const selectedRegions = getSelectedRegions(selectedRegionIds, regions)

  selectedRegions.forEach(selectedRegion => {
    const numberOfProvincesSelected = selectedRegion?.provinces?.length
    const numberOfProvinces = regions.find(r => r.id === selectedRegion.id)?.provinces?.length

    if (numberOfProvincesSelected > 0) {
      names.push(`${selectedRegion?.name} (${numberOfProvincesSelected}/${numberOfProvinces})`)
    } else {
      names.push(selectedRegion.name)
    }
  })

  return truncateList(names, t)
}

export const getBillingTypeName = typeValue => {
  const type = billingTypes.find(t => t.value === typeValue)

  return type?.label
}

export const getBillingStatusName = (statusValues, t) => {
  const statusesToDisplay = billingStatuses.filter(s => statusValues.includes(s.value))

  return truncateList(
    statusesToDisplay.map(s => t(s.label)),
    t
  )
}

export const getShipmentStatusLabels = (statusValues, t) => {
  return truncateList(
    statusValues.map(s => t(shipmentStatusLabels[s])),
    t
  )
}

export const getNormalizedTrackingStatusLabels = (trackingStatusValues, t) => {
  const labelByValue = Object.values(normalizedTrackingStatusMessages).reduce(
    (acc, group) => ({ ...acc, ...group }),
    {}
  )

  return truncateList(
    trackingStatusValues.map(s => t(labelByValue[s])),
    t
  )
}

export const showClearFiltersButton = filters => {
  return Object.values(filters)?.some(filterValue => {
    if (Array.isArray(filterValue)) {
      return filterValue.length > 0
    } else if (typeof filterValue === 'object' && filterValue) {
      return Object.values(filterValue).some(value => !!value)
    }
    return !!filterValue
  })
}

export const getExchangeForOrderStatusName = exchangeForOrderStatus => {
  const statuses = {
    [exchangeForOrderStatusValues.pending]: 'Pendiente',
    [exchangeForOrderStatusValues.created]: 'Creado',
  }

  return statuses[exchangeForOrderStatus]
}

export const getPaymentStatusName = paymentStatus => {
  const statuses = {
    [paymentStatuses.pending]: 'Pendiente',
    [paymentStatuses.completed]: 'Completado',
  }

  return statuses[paymentStatus]
}

export const getRefundStatusName = refundStatus => {
  return refundStatusOptions.find(option => option.value === refundStatus)?.label
}

export const getShippingLabelStatusName = shippingLabelStatus => {
  const statuses = {
    [shippingLabelStatusValues.pending]: 'Pendientes',
    [shippingLabelStatusValues.created]: 'Creadas',
  }

  return statuses[shippingLabelStatus]
}

export const getShipmentExceptionsOptions = (shipmentException, t) => {
  const shipmentExceptionsOptions = {
    true: t('Con alertas pendientes'),
    false: t('Sin alertas pendientes'),
  }

  return shipmentExceptionsOptions[shipmentException]
}

export const getMissingInformationLabel = (missingInformation, t) => {
  const isAlert = ['any', 'none'].includes(missingInformation)

  if (isAlert) {
    return t('Alertas pendientes')
  }

  return t('Acción requerida')
}

export const getMissingInformationValue = (missingInformation, t) => {
  const missingInformationLabels = {
    hs_code: t('HS code incompleto'),
    weight: t('Peso incompleto'),
    country: t('País incompleto'),
    unit_cost: t('Costo unitario incompleto'),
    any: t('Con alertas pendientes'),
    none: t('Sin alertas pendientes'),
    export_details: t('Precios exportación incompletos'),
  }

  return missingInformationLabels[missingInformation]
}

export const getProductStatusLabel = (status, t) => {
  const selectedOption = productStatusOptions.find(option => option.value === status)

  return t(selectedOption?.label)
}

export const getExportPriceStatusLabels = (selectedValues, t) => {
  const selectedOptions = exportPriceStatusOptions.filter(option => selectedValues.includes(option.value))
  const labels = selectedOptions.map(option => t(option.label))

  return truncateList(labels, t)
}

export const getCountryName = (countryCode, regions) => {
  const region = regions.find(r => r.code === countryCode)

  return region?.name
}

export const getCountryNames = (countryCodes, regions, t) => {
  const countryNames = countryCodes.map(countryCode => getCountryName(countryCode, regions))
  const foundCountryNames = countryNames.filter(Boolean)

  return truncateList(foundCountryNames, t)
}

export const getWeightLabel = (weightGte, weightLte) => {
  if (weightLte && weightGte) {
    return `${weightGte} - ${weightLte} gr`
  } else if (weightLte) {
    return `<= ${weightLte} gr`
  } else if (weightGte) {
    return `>= ${weightGte} gr`
  }
  return ''
}

export const getUnitCostLabel = (unitCostGte, unitCostLte) => {
  if (unitCostLte && unitCostGte) {
    return `${unitCostGte} - ${unitCostLte} €`
  } else if (unitCostLte) {
    return `<= ${unitCostLte} €`
  } else if (unitCostGte) {
    return `>= ${unitCostGte} €`
  }
  return ''
}

export const getCategoryLabel = (categoryId, productCategories) => {
  const category = productCategories.find(c => c.id === categoryId)

  return category?.name
}

export const getCarriersLabel = (selectedCarriers, carriers, t) => {
  const names = []

  selectedCarriers?.forEach(carrierId => {
    const carrier = carriers.find(c => c.id === carrierId)
    if (carrier) {
      names.push(carrier.name)
    }
  })

  return truncateList(names, t)
}

export const getAlertTypeLabels = (selectedAlertTypes = [], t) => {
  const names = selectedAlertTypes.map(alertType => t(alertTypeLabels[alertType] || alertType))

  return truncateList(names, t)
}

export const getIsGiftLabel = (isGift, t) => {
  const options = {
    true: t('Sí'),
    false: t('No'),
  }

  return options[isGift]
}

export const getExchangeTypeOptions = (options, t) => {
  let labels = []

  options.forEach(option => {
    const label = exchangedTypesOptions.find(o => o.value === option)?.label
    if (label) {
      labels.push(t(label))
    }
  })

  return truncateList(labels, t)
}

export const getCarrierServiceLabel = (carrierServiceId, carriers) => {
  const carrier = carriers?.find(c => c.id === carrierServiceId)
  return `${carrier?.name} | ${carrier?.service_name}`
}

export const getNamesByIds = (selectedIds, items, t) => {
  const filtered = items.filter(item => selectedIds.includes(item.id))
  return truncateList(
    filtered.map(item => item.name),
    t
  )
}

export const getPolicyRulesNames = (selectedIds, policyRules, t) => {
  const filtered = policyRules.filter(rule => selectedIds.includes(rule.id))

  return truncateList(filtered.map(getPolicyRuleLabel), t)
}

export const getClaimStatusLabels = (selectedClaimStatuses = [], t) => {
  const names = selectedClaimStatuses
    .map(value => claimStatusOptions.find(option => option.value === value)?.label)
    .filter(Boolean)
    .map(label => t(label))

  return truncateList(names, t)
}

export const getClaimTypeLabels = (selectedClaimTypes = [], t) => {
  const names = selectedClaimTypes
    .map(value => claimTypeOptions.find(option => option.value === value)?.label)
    .filter(Boolean)
    .map(label => t(label))

  return truncateList(names, t)
}

export const getParcelTypeLabels = (selectedParcelTypes = [], t) => {
  const names = selectedParcelTypes
    .map(value => parcelTypeOptions.find(option => option.value === value)?.label)
    .filter(Boolean)
    .map(label => t(label))

  return truncateList(names, t)
}

export const getLocationLabel = (selectedLocation, locations) => {
  const location = locations.find(l => l.id === selectedLocation)

  return location?.internal_name
}
